import {
  de,
  deAT,
  enGB,
  enUS,
  es,
  fr,
  it,
  nl,
  pl,
  pt,
  ptBR,
  uk,
  type Locale as DateFnsLocale,
} from "date-fns/locale";

import { defaultLocale, localeCountries, locales, type Locale } from "@/i18n/config";

export interface ResolvedFormatLocale {
  intl: string;
  dateFns: DateFnsLocale;
}

const canonicalizeLocale = (locale: string | undefined): string | undefined => {
  if (!locale) {
    return undefined;
  }

  try {
    return Intl.getCanonicalLocales(locale)[0];
  } catch {
    return undefined;
  }
};

export const isLocale = (locale: string): locale is Locale =>
  locales.some((supportedLocale) => supportedLocale === locale);

/**
 * Normalizes any external or internal locale identifier (e.g. "uk", "uk-UA", "ua")
 * into the application internal Locale ("ua").
 */
export const toAppLocale = (locale: string | null | undefined): Locale => {
  if (!locale) return defaultLocale;
  const lower = locale.toLowerCase().trim();
  if (lower === "uk" || lower === "uk-ua" || lower.startsWith("uk-") || lower.startsWith("uk_")) {
    return "ua";
  }
  if (isLocale(lower)) {
    return lower;
  }
  const primary = lower.split(/[-_]/)[0];
  if (primary === "uk" || primary === "ua") {
    return "ua";
  }
  if (isLocale(primary)) {
    return primary;
  }
  return defaultLocale;
};

/**
 * Converts an internal application locale to the standard BCP-47 / CLDR Intl locale string.
 * Maps internal "ua" to standard "uk-UA".
 */
export const toIntlLocale = (locale: Locale | string | undefined): string => {
  const appLocale = toAppLocale(locale);
  if (appLocale === "ua") {
    return "uk-UA";
  }
  return `${appLocale}-${localeCountries[appLocale]}`;
};

const DATE_FNS_BY_LOCALE: Record<Locale, DateFnsLocale> = {
  fr,
  en: enGB,
  it,
  nl,
  pt,
  de,
  es,
  pl,
  ua: uk,
};

const DATE_FNS_BY_REGIONAL_LOCALE: Record<string, DateFnsLocale> = {
  "de-AT": deAT,
  "en-US": enUS,
  "pt-BR": ptBR,
};

/**
 * Returns the date-fns locale for an internal application locale.
 * Maps internal "ua" to date-fns "uk".
 */
export const toDateFnsLocale = (locale: Locale | string | undefined): DateFnsLocale => {
  const appLocale = toAppLocale(locale);
  return DATE_FNS_BY_LOCALE[appLocale];
};

export const resolveFormatLocale = (
  locale: string | undefined,
  regionalOverride?: string,
): ResolvedFormatLocale => {
  const canonicalLocale = canonicalizeLocale(locale);
  const requestedLocale = canonicalLocale ? new Intl.Locale(canonicalLocale) : undefined;
  const canonicalOverride = canonicalizeLocale(regionalOverride);
  const overrideLocale = canonicalOverride ? new Intl.Locale(canonicalOverride) : undefined;

  const isRequestedValid =
    requestedLocale?.language &&
    (isLocale(requestedLocale.language) || requestedLocale.language === "uk");
  const isOverrideValid =
    overrideLocale?.language &&
    (isLocale(overrideLocale.language) || overrideLocale.language === "uk");

  const activeLocale: Locale = isRequestedValid
    ? toAppLocale(requestedLocale.language)
    : !requestedLocale && isOverrideValid
      ? toAppLocale(overrideLocale.language)
      : defaultLocale;

  const derivedLocale = activeLocale === "ua" ? "uk-UA" : `${activeLocale}-${localeCountries[activeLocale]}`;
  const matchingRegionalLocale = [canonicalOverride, canonicalLocale].find((candidate) => {
    if (!candidate) {
      return false;
    }

    const parsedLocale = new Intl.Locale(candidate);
    const candidateLanguage = toAppLocale(parsedLocale.language);
    return parsedLocale.region && candidateLanguage === activeLocale;
  });
  const intl = matchingRegionalLocale ?? derivedLocale;

  return {
    intl,
    dateFns: DATE_FNS_BY_REGIONAL_LOCALE[intl] ?? DATE_FNS_BY_LOCALE[activeLocale],
  };
};
